import { Fragment, useEffect, useState } from 'react'
import {
  Box, Button, Grid, GridItem, Heading, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Text,
  Step, StepDescription, StepIcon, StepIndicator, StepNumber, StepSeparator, StepStatus, StepTitle, Stepper, useSteps, FormControl, FormLabel, ButtonGroup, VStack, Textarea
} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import { useImmer } from 'use-immer'
import { convertAdhocQuoteToProjectById } from 'api'
import { useAppContext } from 'hooks/useAppContext'
import { AccountingInfoStep, defaultAccountingInfo } from 'components/quotes/AccountingInfoStep'

const steps = [
  { title: 'First', description: 'Client Info' },
  { title: 'Second', description: 'Quote Options' },
  { title: 'Third', description: 'Accounting Info' },
  { title: 'Fourth', description: 'Project Overview' }
]

export const ModalConvertToProject = ({ quote, customer, isOpen, onClose }) => {
  const { createToast } = useAppContext()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [form, setForm] = useImmer({
    clientInfo: {
      company: '',
      contactName: '',
      email: '',
      phone: '',
      address: '',
      state: '',
      city: '',
      zip: ''
    },
    quoteOptions: {
      additionalItems: []
    },
    scope: '',
    signedQuote: null,
    signedArt: null,
    taxExemption: null,
    accountingInfo: defaultAccountingInfo()
  })

  const { activeStep, setActiveStep } = useSteps({
    index: 1,
    count: steps.length
  })

  useEffect(() => {
    if (!customer?.id) return
    setForm(draft => {
      draft.clientInfo = {
        company: customer.company || '',
        contactName: `${customer.firstname || ''} ${customer.lastname || ''})`,
        email: customer.email || '',
        phone: customer.phone || '',
        address: customer.address || '',
        state: customer.state || '',
        city: customer.city || '',
        zip: customer.zip || ''
      }
    })
  }, [customer?.id])

  useEffect(() => {
    if (!quote?.id) return

    setActiveStep(1)

    const quoteOptions = {}
    quoteOptions.additionalItems = quote.additionalItems ? JSON.parse(quote.additionalItems) : []
    setForm(draft => {
      draft.quoteOptions = quoteOptions
      draft.scope = ''
      draft.signedQuote = null
      draft.signedArt = null
      draft.taxExemption = null
      draft.accountingInfo = defaultAccountingInfo(quote.paymentTerms || '')
    })
  }, [quote?.id])

  const handleSubmit = async () => {
    setIsSubmitting(true)
    try {
      const project = await convertAdhocQuoteToProjectById(quote?.id, { ...form })
      createToast('success', 'Project created.')
      setIsSubmitting(false)
      window.open(`/projects/detail/${project.id}`)
      onClose()
    } catch (err) {
      createToast('error', 'Error', err.response.data)
      setIsSubmitting(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Text fontSize="xl">Convert To Project. Quote #{quote?.quoteNo}</Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Stepper index={activeStep} mb={5}>
            {steps.map((step, index) => (
              <Step key={index}>
                <StepIndicator>
                  <StepStatus
                    complete={<StepIcon />}
                    incomplete={<StepNumber />}
                    active={<StepNumber />}
                  />
                </StepIndicator>

                <Box flexShrink='0'>
                  <StepTitle>{step.title}</StepTitle>
                  <StepDescription>{step.description}</StepDescription>
                </Box>

                <StepSeparator />
              </Step>
            ))}
          </Stepper>
          {activeStep === 1 && (
            <FirstStep
              form={form}
              setForm={setForm}
              onNext={() => setActiveStep(2)}
            />
          )}
          {activeStep === 2 && (
            <SecondStep
              form={form}
              setForm={setForm}
              onPrev={() => setActiveStep(1)}
              onNext={() => setActiveStep(3)}
            />
          )}
          {activeStep === 3 && (
            <AccountingInfoStep
              form={form}
              setForm={setForm}
              onPrev={() => setActiveStep(2)}
              onNext={() => setActiveStep(4)}
            />
          )}
          {activeStep === 4 && (
            <ThirdStep
              form={form}
              setForm={setForm}
              onPrev={() => setActiveStep(3)}
              onNext={handleSubmit}
              isSubmitting={isSubmitting}
            />
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

ModalConvertToProject.propTypes = {
  quote: PropTypes.object,
  customer: PropTypes.object,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func
}

const FirstStep = ({ form, setForm, onNext, ...rest }) => {
  const { clientInfo } = form

  const handleChange = (e) => {
    setForm(draft => {
      draft.clientInfo = {
        ...draft.clientInfo,
        [e.target.name]: e.target.value
      }
    })
  }
  const handleSubmit = (e) => {
    e.preventDefault()
    onNext()
  }

  return (
    <Box {...rest}>
      <form onSubmit={handleSubmit}>
        <Grid
          templateColumns="1fr 1fr"
          columnGap={5}
          rowGap={2}
        >
          <GridItem>
            <FormControl isRequired>
              <FormLabel>Company Name</FormLabel>
              <Input name="company" value={clientInfo.company} onChange={handleChange} />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired>
              <FormLabel>Phone Number</FormLabel>
              <Input name="phone" value={clientInfo.phone} onChange={handleChange} />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired>
              <FormLabel>Contact Name</FormLabel>
              <Input name="contactName" value={clientInfo.contactName} onChange={handleChange} />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired>
              <FormLabel>Email Address</FormLabel>
              <Input name="email" value={clientInfo.email} onChange={handleChange} />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired>
              <FormLabel>Address</FormLabel>
              <Input name="address" value={clientInfo.address} onChange={handleChange} />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired>
              <FormLabel>State</FormLabel>
              <Input name="state" value={clientInfo.state} onChange={handleChange} />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired>
              <FormLabel>City</FormLabel>
              <Input name="city" value={clientInfo.city} onChange={handleChange} />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired>
              <FormLabel>Zip</FormLabel>
              <Input name="zip" value={clientInfo.zip} onChange={handleChange} />
            </FormControl>
          </GridItem>
        </Grid>
        <ButtonGroup w="100%" justifyContent="flex-end" mt={5}>
          <Button type="button" size="sm" colorScheme="blue" isDisabled={true}>Previous</Button>
          <Button type="submit" size="sm" colorScheme="blue">Next</Button>
        </ButtonGroup>
      </form>
    </Box>
  )
}

FirstStep.propTypes = {
  form: PropTypes.object,
  setForm: PropTypes.func,
  onNext: PropTypes.func
}

const SecondStep = ({ form, setForm, onPrev, onNext, ...rest }) => {
  const handleFileChange = (e) => {
    const { name, files } = e.target
    setForm(draft => {
      draft[name] = files?.[0] || null
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    onNext()
  }

  return (
    <Box {...rest}>
      <form onSubmit={handleSubmit}>
        <VStack gap={5}>
          <AdditionalItems
            form={form}
            setForm={setForm}
          />
          <Box w="100%">
            <Grid templateColumns="1fr 1fr" columnGap={5}>
              <GridItem>
                <FormControl isRequired>
                  <FormLabel>Signed Quote</FormLabel>
                  <Input type="file" name="signedQuote" accept=".pdf" onChange={handleFileChange} p={1} />
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl>
                  <FormLabel>Signed Art</FormLabel>
                  <Input type="file" name="signedArt" accept=".pdf" onChange={handleFileChange} p={1} />
                </FormControl>
              </GridItem>
            </Grid>
          </Box>
        </VStack>
        <ButtonGroup w="100%" justifyContent="flex-end" mt={5}>
          <Button type="button" size="sm" colorScheme="blue" onClick={onPrev}>Previous</Button>
          <Button type="submit" size="sm" colorScheme="blue">Next</Button>
        </ButtonGroup>
      </form>
    </Box>
  )
}

SecondStep.propTypes = {
  form: PropTypes.object,
  setForm: PropTypes.func,
  onPrev: PropTypes.func,
  onNext: PropTypes.func
}

const AdditionalItems = ({ form, setForm, ...rest }) => {
  const { quoteOptions } = form

  return (
    <Box w="100%" {...rest}>
      <Heading size="md" mb={5}>Additional Items</Heading>
      <Grid
        templateColumns="repeat(4, 1fr)"
        columnGap={5}
        rowGap={2}
      >
        {!quoteOptions.additionalItems.length && (
          <Text fontSize="sm">No items.</Text>
        )}
        {quoteOptions.additionalItems.map(item => {
          return (
            <Fragment key={item.id}>
              <GridItem colSpan={1}>
                <FormControl>
                  <FormLabel>Category</FormLabel>
                  <Input value={item.category} readOnly />
                </FormControl>
              </GridItem>
              <GridItem colSpan={3}>
                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Input value={item.description} readOnly />
                </FormControl>
              </GridItem>
            </Fragment>
          )
        })}
      </Grid>
    </Box>
  )
}

AdditionalItems.propTypes = {
  form: PropTypes.object,
  setForm: PropTypes.func
}

const ThirdStep = ({ form, setForm, onPrev, onNext, isSubmitting, ...rest }) => {
  const handleSubmit = (e) => {
    e.preventDefault()
    onNext()
  }

  return (
    <Box {...rest}>
      <form onSubmit={handleSubmit}>
        <FormControl isRequired>
          <FormLabel>Scope</FormLabel>
          <Textarea name="scope" value={form.scope} onChange={e => setForm(draft => { draft.scope = e.target.value })} />
        </FormControl>
        <ButtonGroup w="100%" justifyContent="flex-end" mt={5}>
          <Button type="button" size="sm" colorScheme="blue" onClick={onPrev}>Previous</Button>
          <Button type="submit" size="sm" colorScheme="blue" isLoading={isSubmitting}>Finish</Button>
        </ButtonGroup>
      </form>
    </Box>
  )
}

ThirdStep.propTypes = {
  form: PropTypes.object,
  setForm: PropTypes.func,
  onPrev: PropTypes.func,
  onNext: PropTypes.func,
  isSubmitting: PropTypes.bool
}
